import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { homeFor } from "@/lib/roles";
import InstallBanner from "@/components/InstallBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Egg, ShieldCheck, HardHat, ClipboardCheck, Truck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(homeFor(u.role));
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const quick = (em, pw) => {
    setEmail(em);
    setPassword(pw);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-950">
      {/* Visual side */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1506976785307-8732e854ad03?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
          alt="Œufs de ferme calibrés"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-900/40" />
        <div className="absolute bottom-0 left-0 p-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-amber-400 flex items-center justify-center">
              <Egg className="w-7 h-7 text-slate-900" />
            </div>
            <span className="font-heading font-black text-3xl uppercase text-white tracking-tight">
              Ferme d'Autheuil
            </span>
          </div>
          <p className="text-slate-300 max-w-md font-medium">
            Traçabilité des œufs, étiquettes palettes, bons de livraison et bilan mensuel.
            LIOT Autheuil — Rue de la Haute Boulaye, 27490 Autheuil-Authouillet.
          </p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl bg-amber-400 flex items-center justify-center">
              <Egg className="w-6 h-6 text-slate-900" />
            </div>
            <span className="font-heading font-black text-2xl uppercase text-white tracking-tight">
              Ferme d'Autheuil
            </span>
          </div>

          <h1 className="font-heading font-black text-3xl uppercase text-white tracking-tight">
            Identification
          </h1>
          <p className="text-slate-400 text-sm mb-6 font-medium">
            Accès Opérateur Entrepôt ou Administrateur Exploitation
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wide font-bold">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 h-12 bg-slate-800 border-slate-700 text-white"
                placeholder="admin@autheuil.fr"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wide font-bold">Mot de passe</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 h-12 bg-slate-800 border-slate-700 text-white"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-rose-400 text-sm font-semibold bg-rose-950/40 border border-rose-800 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold text-base"
            >
              {loading ? "Connexion…" : "Se connecter"}
            </Button>
          </form>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <button
              data-testid="login-quick-admin-btn"
              onClick={() => quick("admin@autheuil.fr", "Autheuil2026!")}
              className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-2 text-slate-300 hover:bg-slate-800 text-sm font-semibold"
            >
              <ShieldCheck className="w-4 h-4 text-emerald-400" /> Admin
            </button>
            <button
              data-testid="login-quick-operator-btn"
              onClick={() => quick("operateur@autheuil.fr", "Operateur2026!")}
              className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-2 text-slate-300 hover:bg-slate-800 text-sm font-semibold"
            >
              <HardHat className="w-4 h-4 text-amber-400" /> Opérateur
            </button>
            <button
              data-testid="login-quick-responsable-btn"
              onClick={() => quick("responsable@autheuil.fr", "Responsable2026!")}
              className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-2 text-slate-300 hover:bg-slate-800 text-sm font-semibold"
            >
              <ClipboardCheck className="w-4 h-4 text-sky-400" /> Responsable
            </button>
            <button
              data-testid="login-quick-chauffeur-btn"
              onClick={() => quick("chauffeur@autheuil.fr", "Chauffeur2026!")}
              className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-2 text-slate-300 hover:bg-slate-800 text-sm font-semibold"
            >
              <Truck className="w-4 h-4 text-rose-400" /> Chauffeur
            </button>
          </div>
          <div className="mt-6">
            <InstallBanner dark />
          </div>
        </div>
      </div>
    </div>
  );
}
